import React from 'react'
import { useAccessContext } from '../../context/AccessContext'
import type { BillingEntitlement } from '../../types/access'

const fallbackEntitlement = (featureKey: string, title: string): BillingEntitlement => ({
  featureKey,
  state: 'disabled',
  enabled: false,
  included: false,
  reasonCode: 'not_configured',
  planKey: null,
  planName: null,
  limit: null,
  usage: null,
  remaining: null,
  upgradeAction: 'contact_sales',
  contactSalesUrl: `/demo-request?source=billing_feature_gate&campaign=${encodeURIComponent(featureKey)}&featureKey=${encodeURIComponent(featureKey)}`,
  message: `${title} is not configured for the current plan.`,
})

export const BillingFeatureGate: React.FC<{
  featureKey: string
  title: string
  description: string
  children?: React.ReactNode
}> = ({ featureKey, title, description, children }) => {
  const { accessContext, currentOrganization, loading } = useAccessContext()
  if (loading) {
    return <section className="panel workspace-panel billing-feature-gate" aria-busy="true"><p>Checking plan access…</p></section>
  }

  const entitlement = accessContext?.billingEntitlements?.features?.[featureKey]
    || fallbackEntitlement(featureKey, title)
  if (entitlement.enabled) return <>{children}</>

  const openContactSales = () => {
    const base = entitlement.contactSalesUrl || fallbackEntitlement(featureKey, title).contactSalesUrl || '/demo-request'
    const url = new URL(base, window.location.origin)
    url.searchParams.set('source', 'billing_feature_gate')
    url.searchParams.set('campaign', featureKey)
    url.searchParams.set('featureKey', featureKey)
    url.searchParams.set('featureName', title)
    if (entitlement.planKey) url.searchParams.set('currentPlan', entitlement.planKey)
    if (currentOrganization?.id) url.searchParams.set('organizationId', currentOrganization.id)
    url.searchParams.set('returnPath', `${window.location.pathname}${window.location.search}`)
    window.location.assign(`${url.pathname}${url.search}`)
  }

  return (
    <section className="panel workspace-panel billing-feature-gate stack" data-testid={`billing-feature-gate-${featureKey}`}>
      <div className="billing-feature-gate__lock" aria-hidden="true">🔒</div>
      <div className="stack" style={{ gap: '.4rem' }}>
        <p className="billing-feature-gate__eyebrow">Contact Sales</p>
        <h2>{title}</h2>
        <p>{entitlement.message}</p>
        <p className="text-muted">{description}</p>
        {entitlement.state === 'usage_exceeded' && entitlement.limit !== null ? (
          <p><strong>{entitlement.usage ?? 0}</strong> used of <strong>{entitlement.limit}</strong>.</p>
        ) : null}
      </div>
      <div className="billing-feature-gate__actions">
        <button type="button" className="btn btn-primary" onClick={openContactSales}>Contact Sales</button>
        {entitlement.upgradeAction === 'select_plan' || entitlement.upgradeAction === 'checkout' ? (
          <a className="btn btn-secondary" href="/billing">View Plans</a>
        ) : null}
      </div>
    </section>
  )
}
